import type { FormValue, Question } from '../../types';
import type { RepeatBindings } from '../primitives/repeatBindings';
import type { LayoutNode } from './types';
import {
  tableSlotNodes,
  walkLayoutStructureChildren,
} from './layoutStructureChildLists';
import { conditionalWhenSatisfied } from './whenClause';

export type ActiveLayoutWalkContext = {
  answers: Record<string, FormValue>;
  repeatBindings?: RepeatBindings;
  /** Sum ids seen so far; mutated when the visitor handles `sum` nodes. */
  derived: Record<string, number>;
  schemaMap?: ReadonlyMap<string, Question>;
};

/**
 * Walks layout nodes on the active branches only (matches {@link BespokeLayoutRenderer}
 * `Conditional` and {@link computeLayoutDerivedTotals}).
 */
export function walkActiveLayoutNodes(
  nodes: LayoutNode[],
  ctx: ActiveLayoutWalkContext,
  visit: (node: LayoutNode) => void
): void {
  const repeatBindings = ctx.repeatBindings ?? {};

  const walk = (arr: LayoutNode[]) => {
    for (const node of arr) {
      visit(node);

      if (node.type === 'conditional') {
        const active = conditionalWhenSatisfied(
          node,
          ctx.answers,
          repeatBindings,
          ctx.derived,
          ctx.schemaMap
        );
        if (active) {
          walk(node.children);
        } else if (node.elseChildren) {
          walk(node.elseChildren);
        }
      } else if (node.type === 'table') {
        // Headers sit outside every row, so nothing gates them.
        walk(tableSlotNodes(node.rowHeader));
        for (const header of node.colHeaders ?? []) {
          walk(tableSlotNodes(header));
        }
        for (const row of node.rows) {
          if (
            conditionalWhenSatisfied(
              row,
              ctx.answers,
              repeatBindings,
              ctx.derived,
              ctx.schemaMap
            )
          ) {
            walk(tableSlotNodes(row.label));
            walk(row.cells);
          }
        }
      } else {
        walkLayoutStructureChildren(node, walk);
      }
    }
  };

  walk(nodes);
}
